import type { InteractiveObject } from '../interactive-object';
import type { InteractiveControl } from './interactive-control';

/**
 * Properties that can override the appearance of controls during rendering
 */
export type ControlRenderingStyleOverride = Partial<
  Pick<
    InteractiveObject,
    | 'cornerStyle'
    | 'cornerSize'
    | 'cornerColor'
    | 'cornerStrokeColor'
    | 'cornerDashArray'
    | 'transparentCorners'
  >
>;

const TWO_PI = Math.PI * 2;

function resolveCornerSize(
  control: InteractiveControl,
  style: ControlRenderingStyleOverride | undefined,
  object: Pick<InteractiveObject, 'cornerSize'>,
) {
  return control.sizeX || style?.cornerSize || object.cornerSize;
}

function resolveTransparentCorners(
  style: ControlRenderingStyleOverride | undefined,
  object: Pick<InteractiveObject, 'transparentCorners'>,
) {
  return typeof style?.transparentCorners === 'boolean'
    ? style.transparentCorners
    : object.transparentCorners;
}

/**
 * Renders a circular control
 */
export function renderCircleControl(
  this: InteractiveControl,
  ctx: CanvasRenderingContext2D,
  left: number,
  top: number,
  styleOverride: ControlRenderingStyleOverride | undefined,
  targetObject: InteractiveObject,
): void {
  const style = styleOverride || {};
  const xSize = resolveCornerSize(this, style, targetObject);
  const ySize = this.sizeY || style.cornerSize || targetObject.cornerSize;
  const transparentCorners = resolveTransparentCorners(style, targetObject);
  const stroke =
    !transparentCorners &&
    (style.cornerStrokeColor || targetObject.cornerStrokeColor || '').length > 0;

  ctx.save();
  ctx.fillStyle = style.cornerColor || targetObject.cornerColor || '';
  ctx.strokeStyle = style.cornerStrokeColor || targetObject.cornerStrokeColor || '';

  let drawLeft = left;
  let drawTop = top;
  let size = xSize;

  if (xSize > ySize) {
    size = xSize;
    ctx.scale(1, ySize / xSize);
    drawTop = (top * xSize) / ySize;
  } else if (ySize > xSize) {
    size = ySize;
    ctx.scale(xSize / ySize, 1);
    drawLeft = (left * ySize) / xSize;
  }

  ctx.beginPath();
  ctx.arc(drawLeft, drawTop, size / 2, 0, TWO_PI, false);

  if (transparentCorners) {
    ctx.stroke();
  } else {
    ctx.fill();
    if (stroke) {
      ctx.stroke();
    }
  }

  ctx.restore();
}

/**
 * Renders a square control
 */
export function renderSquareControl(
  this: InteractiveControl,
  ctx: CanvasRenderingContext2D,
  left: number,
  top: number,
  styleOverride: ControlRenderingStyleOverride | undefined,
  targetObject: InteractiveObject,
): void {
  const style = styleOverride || {};
  const xSize = resolveCornerSize(this, style, targetObject);
  const ySize = this.sizeY || style.cornerSize || targetObject.cornerSize;
  const transparentCorners = resolveTransparentCorners(style, targetObject);
  const stroke =
    !transparentCorners &&
    (style.cornerStrokeColor || targetObject.cornerStrokeColor || '').length > 0;
  const xSizeBy2 = xSize / 2;
  const ySizeBy2 = ySize / 2;

  ctx.save();
  ctx.fillStyle = style.cornerColor || targetObject.cornerColor || '';
  ctx.strokeStyle = style.cornerStrokeColor || targetObject.cornerStrokeColor || '';
  ctx.translate(left, top);
  ctx.rotate((targetObject.getTotalAngle() * Math.PI) / 180);

  if (transparentCorners) {
    ctx.strokeRect(-xSizeBy2, -ySizeBy2, xSize, ySize);
  } else {
    ctx.fillRect(-xSizeBy2, -ySizeBy2, xSize, ySize);
    if (stroke) {
      ctx.strokeRect(-xSizeBy2, -ySizeBy2, xSize, ySize);
    }
  }

  ctx.restore();
}
